import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../../api";
import { PlanBadge } from "../../../components/badge";
import { Button } from "../../../components/button";
import { Card, CardContent, CardGrid, CardHeader } from "../../../components/card";
import { CommandBar } from "../../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../../components/dashboard";
import { DateTimeLabel } from "../../../components/datetime-label";
import { Icon } from "../../../components/icon";
import { Section } from "../../../components/layout";
import { Link } from "../../../components/link";
import { Meter } from "../../../components/meter";
import { DefaultRow, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from "../../../components/table";
import { InlineCode, MutedText } from "../../../components/text";
import { Information } from "../../../components/warn";
import { formatDuration, formatFloat, formatNumber } from "../../../ui/format";
import { runOperation } from "../../../ui/operation";

export default function (ctx) {
    const accountId = ctx.params.accountId;
    const rateLimits = el.state.create(null);

    function loadRateLimits() {
        runOperation("Loading rate limits...", async () => {
            const result = await api.getJson(`/api/v1/admin/accounts/${accountId}/rate-limits`);
            rateLimits.value = result.data;
        });
    }

    loadRateLimits();

    return Dashboard(
        Section(
            Link(el.fragment(Icon("arrow-left-line"), "Return back"), null, {
                onClick() {
                    history.go(-1);
                    page();
                }
            })
        ),
        el.state.listening([rateLimits], () =>
            DashboardTitle(
                "Rate limits",
                rateLimits.value ? `Current rate limiter state of the ${rateLimits.value.name} account.` : "Loading account rate limits."
            )
        ),
        Section(
            CommandBar(
                Button(Icon("refresh-line"), "Refresh", {
                    onClick() {
                        loadRateLimits();
                    }
                })
            )
        ),
        el.state.listening([rateLimits], () => rateLimits.value && el.fragment(
            Section(
                Information(`Values are read from the in-memory rate limiter cache of this server instance and reset when it restarts. Hits are kept for the last ${rateLimits.value.hitsRetentionHours} hours.`)
            ),
            Section(
                CardGrid(
                    Card(
                        CardHeader("Current plan"),
                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, PlanBadge(rateLimits.value.plan)))
                    ),
                    Card(
                        CardHeader("Limit multiplier"),
                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } },
                            rateLimits.value.limitMultiplier == null ? "-" : `${formatFloat(rateLimits.value.limitMultiplier)}x`))
                    ),
                    Card(
                        CardHeader("Active windows"),
                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, formatNumber(rateLimits.value.windows.filter(window => window.currentUsage > 0).length)))
                    ),
                    Card(
                        CardHeader("Limit hits"),
                        CardContent(el("h2", { style: { margin: 0, fontWeight: 400 } }, formatNumber(rateLimits.value.windows.reduce((sum, window) => sum + window.hits, 0))))
                    )
                )
            ),
            Section(
                Card(
                    Table(
                        { classList: ["hoverable"], pagination: { perPage: 50 } },
                        TableHeader(
                            TableHeaderCell("Limiter"),
                            TableHeaderCell("Window"),
                            TableHeaderCell("Usage"),
                            TableHeaderCell("Consumption"),
                            TableHeaderCell("Hits"),
                            TableHeaderCell("Last hit")
                        ),
                        TableBody(
                            [...rateLimits.value.windows]
                                .sort((a, b) => b.hits - a.hits || b.currentUsage / b.threshold - a.currentUsage / a.threshold)
                                .map(window =>
                                    TableRow(
                                        TableCell(InlineCode(window.name)),
                                        TableCell(formatDuration(window.durationSeconds)),
                                        TableCell(formatNumber(window.currentUsage), " / ", formatNumber(window.threshold)),
                                        TableCell(Meter({ value: window.currentUsage / window.threshold * 100, colorShade: true, props: { classList: ["full-width"] } })),
                                        TableCell(window.hits > 0 ? formatNumber(window.hits) : MutedText("-")),
                                        TableCell(window.lastHitAt ? DateTimeLabel(window.lastHitAt) : MutedText("-"))
                                    )
                                )?.nullWhenEmpty() || DefaultRow("No rate limiter activity for this account."),
                            {
                                mount() {
                                    this.closest("table").refreshPagination();
                                }
                            }
                        )
                    )
                )
            )
        ))
    );
}
